/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * AssignmentProgressCard
 *
 * @file AssignmentProgressCard.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import useAssignmentProgressCardStyles from './styles/AssignmentProgressCardStyles';
import { THEMES } from '../../common/constants';
import CustomCard from '../common/CustomCard';
import { BadgeTemplate, TextChipTemplate } from '../tabular/components/Template';
import ProgressBarWidget from '../common/ProgressBarWidget';
import HtmlTooltip, { StatusTemplate } from '../common/HtmlTooltip';
import mixins from '../../assets/styles/mixins.scss';
import TooltipWrapper from '../tooltip/TooltipWrapper';

const AssignmentProgressCard = (props) => {
  const classes = useAssignmentProgressCardStyles(props);
  const {
    title,
    theme,
    sections,
    footer,
    status,
    segments,
    tooltipSegments,
    onCardClick,
    onStatusHover,
    onProgressBarHover,
    progressBarButtonTooltip
  } = props;

  const [isOverflowing, setIsOverflowing] = useState(true);
  const titleRef = useRef(null);

  useEffect(() => {
    const element = titleRef.current;
    if (element) {
      const originalHeight = element.clientHeight;
      element.style.webkitLineClamp = 'unset';
      setTimeout(() => {
        const fullHeight = element.clientHeight;
        setIsOverflowing(fullHeight > originalHeight);
        element.style.webkitLineClamp = 2;
      }, 0);
    }
  }, [titleRef.current?.clientHeight]);

  const renderTitle = () => {
    if (!title) return null;

    return (
      <TooltipWrapper
        tooltip={title}
        arrow
        interactive
        showTooltip={isOverflowing}
      >
        <span ref={titleRef} className={classes.title}>{title}</span>
      </TooltipWrapper>
    );
  };

  const renderStatus = () => {
    if (!status?.label) return null;

    const isDarkTheme = theme === THEMES.DARK;
    const statusId = isDarkTheme ? `${status.id}-dark` : status.id;

    return (
      <div
        onMouseEnter={() => {
          onStatusHover(props);
        }}
      >
        <BadgeTemplate
          topics={null}
          status={statusId}
          label={status.label}
          showIcon={status?.showIcon || false}
          classes={{ badge: classes.badge }}
        />
      </div>
    );
  };

  const renderHeader = () => (
    <div className={classes.header}>
      {renderTitle()}
      {renderStatus()}
    </div>
  );

  const renderSections = () => {
    if (!sections) return null;

    return (
      <TextChipTemplate
        sections={sections.sections}
        label={sections.label}
        classes={{
          sectionsContainer: classes.sectionsContainer,
          sectionsLabel: classes.sectionsLabel,
          sectionChip: classes.sectionChip,
          sectionNumber: classes.sectionNumber,
          textContainer: classes.sectionTextContainer
        }}
      />
    );
  };

  const renderSegments = () => {
    if (!segments || segments.length === 0 || !tooltipSegments || tooltipSegments.length === 0) return null;

    const isDarkTheme = theme === THEMES.DARK;
    const segmentsProcessed = segments.map((segment) => ({
      ...segment,
      id: isDarkTheme ? `${segment.id}-dark` : segment.id
    }));

    return (
      <HtmlTooltip
        title={
          <StatusTemplate segments={tooltipSegments} isPercentage={false} />
        }
        arrow
        placement="bottom"
      >
        <button
          type="button"
          className={classes.progressBarWrapperButton}
          onFocus={(e) => {
            e.target.setAttribute('aria-expanded', true);
          }}
          onBlur={(e) => {
            e.target.setAttribute('aria-expanded', false);
          }}
          aria-label={progressBarButtonTooltip}
        >
          <div className={classes.progressBar}>
            <ProgressBarWidget
              segments={segmentsProcessed}
              backgroundColor={isDarkTheme ? mixins.darkProgressBgColor : mixins.accentColor}
              classes={{ progressBarContainer: classes.progressBarContainer }}
              onHover={() => onProgressBarHover(props)}
            />
          </div>
        </button>
      </HtmlTooltip>
    );
  };

  const renderContent = () => (
    <div className={classes.content}>
      {renderSections()}
      {renderSegments()}
    </div>
  );

  const renderFooterBadge = () => {
    if (!footer.badge) return null;

    return (
      <TextChipTemplate
        sections={[footer.badge]}
        classes={{
          sectionsContainer: classes.sectionsContainer,
          sectionsLabel: classes.sectionsLabel,
          sectionChip: classes.sectionChip,
          sectionNumber: classes.sectionNumber,
          textContainer: classes.sectionTextContainer
        }}
      />
    );
  };

  const renderFooterRightText = () => {
    if (!footer.rightText) return null;

    return (
      <Typography className={classes.footerRightText}>{footer.rightText}</Typography>
    );
  };

  const renderFooter = () => (
    <div className={classes.footer}>
      {renderFooterBadge()}
      {renderFooterRightText()}
    </div>
  );

  return (
    <CustomCard
      classes={{ root: `${classes.card} theme-${theme || THEMES.DARK}` }}
      isButtonCard
      onClick={() => onCardClick(props)}
    >
      {renderHeader()}
      {renderContent()}
      {renderFooter()}
    </CustomCard>
  );
};

AssignmentProgressCard.propTypes = {
  title: PropTypes.string,
  theme: PropTypes.string,
  sections: PropTypes.shape({
    label: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.string)
  }),
  footer: PropTypes.shape({
    badge: PropTypes.string,
    rightText: PropTypes.string
  }),
  status: PropTypes.shape({
    label: PropTypes.string,
    id: PropTypes.string,
    showIcon: PropTypes.bool
  }),
  segments: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
    type: PropTypes.string
  })),
  tooltipSegments: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
    type: PropTypes.string
  })),
  onCardClick: PropTypes.func,
  onStatusHover: PropTypes.func,
  onProgressBarHover: PropTypes.func,
  progressBarButtonTooltip: PropTypes.string
};

AssignmentProgressCard.defaultProps = {
  title: '',
  theme: THEMES.DARK,
  sections: null,
  footer: null,
  status: null,
  segments: null,
  tooltipSegments: null,
  onCardClick: () => {},
  onStatusHover: () => {},
  onProgressBarHover: () => {},
  progressBarButtonTooltip: 'Performance Progress Bar'
};

export default AssignmentProgressCard;
